import { Fragment, useCallback } from "react";
import { z } from "zod";
import { createLocalStore } from "../../../lib/store";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useApiQuery } from "../../../services/api";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { toChatSource } from "../../../domains/gpt/chatSource";
import { SourceCard } from "../../ui/ChatSources";
import { AnimatePresence, motion } from "motion/react";
import GPTConversationTabs, {
  type ConversationTurn,
} from "./GPTConversationTabs";
import { GPTToolStep } from "./GPTToolStep";
import GPTChatError from "./GPTChatError";
import { ChatActions } from "./ChatActions";
import ContentMarkdown, { type CitationResolver } from "../../ContentMarkdown";
import {
  citationNumbering,
  citedBesidesPaper,
} from "../../../domains/gpt/citation";
import GPTSourceSheet, { useSelectedGptSource } from "./GPTSourceSheet";
import { ChatInput } from "../../ui/ChatInput";
import { ChatOptions } from "../../ui/ChatOptions";
import { TopicSuggestionCard } from "./TopicSuggestionCard";
import { RoomSuggestionCard } from "./RoomSuggestionCard";
import { VisualAbstractSuggestionCard } from "./VisualAbstractSuggestionCard";
import { PushSuggestionCard } from "./PushSuggestionCard";
import { AskUserCard } from "./AskUserCard";
import { PreAnswer } from "./PreAnswer";
import { ScrollDownButton } from "../../ui/ScrollDownButton";
import { FollowUpQuestionCard } from "../../ui/FollowUpQuestionCard";
import KeyboardAvoidingView from "../../layout/KeyboardAvoidingView";
import { useKeyboardHeight } from "../../../hooks/use-keyboard-height";
import { useMeasuredHeight } from "../../../hooks/use-measured-height";
import { cn } from "../../../lib/utils";
import {
  GPT_BAND_ENTRANCE_SECONDS,
  GPT_DESKTOP_COMPOSER,
  GptDesktopNotice,
} from "../layout";
import { isDesktopShell } from "../../../common/hooks/useIsDesktopView";
import { ChatMessageCard } from "../../ui/ChatMessageCard";
import { ChatMessageMenu } from "./ChatMessageMenu";
import { ChatActionsDropdown } from "../../ui/ChatActionsDropdown";
import { ChatLiveProgress } from "../../ui/ChatLiveProgress";
import { CalloutPanel } from "../../ui/CalloutPanel";
import {
  cleanAnswer,
  GPT_ANSWER_CLASS,
  parseAnswerBlocks,
} from "../../../domains/gpt/answer";
import { useStreamDuration } from "../../../domains/gpt/hooks/useStreamDuration";
import {
  gptSearchCount,
  type GptSourceRef,
} from "../../../domains/gpt/transcript";
import {
  deriveStreamState,
  uiMessageText,
  uiMessageCards,
  uiMessageRequest,
  uiMessageQuestionCount,
  uiMessageSources,
  uiMessageActions,
  uiMessagePhase,
  uiMessageDraft,
  uiMessagePreAnswer,
  uiMessageSourceTotal,
  type GptChatInstance,
  type GeneralChatUIMessage,
} from "../../../domains/gpt/hooks/useGptChat";
import { useChatComposer } from "../../../domains/gpt/hooks/useChatComposer";
import { useChatComposerAdd } from "../../../domains/gpt/hooks/useChatComposerAdd";
import { useGptMode } from "../../../domains/gpt/stores/modeStore";
import { GptModeBadge } from "./GptModeBadge";
import type { GptPanelMode } from "./GptContextPanel";
import { EvidenceAnswer } from "../evidence/EvidenceAnswer";
import { EvidenceDetailsPanel } from "../evidence/EvidenceDetailsPanel";
import { AssistantEvidenceCheckPanel } from "../evidence/AssistantEvidencePanel";
import type { ShownEvidenceCheck } from "../../../domains/gpt/evidenceCheck";
import AssistantSourcesList from "./AssistantSourcesList";
import {
  EvidenceSourcesList,
  evidenceSourceCount,
} from "../evidence/EvidenceSourcesList";
import {
  GptSearchTurn,
  type GptSearchDoc,
  type GptSearchPubmedDoc,
} from "./GptSearchTurn";
import type {
  EvidenceGraphView,
  EvidenceSourceView,
} from "../../../domains/gpt/hooks/useEvidenceGptChat";
import { useDictationControl } from "../../../domains/gpt/hooks/useDictationControl";
import {
  ACCEPTED_FILE_TYPES,
  ChatAttachmentChips,
  ChatAttachmentList,
} from "./ChatAttachments";
import {
  ChatComposerAddPanel,
  ChatComposerAddTrigger,
} from "./ChatComposerAdd";
import {
  ChatReferenceChips,
  ChatReferenceList,
  ChatMentionDropdown,
} from "./ChatReferences";
import { useChatMention } from "../../../domains/gpt/hooks/useChatMention";
import { ChatAttachmentPreview } from "./ChatAttachmentPreview";
import type { ChatAttachments as ChatAttachmentsState } from "../../../domains/gpt/hooks/useChatAttachments";
import type { ChatReferences as ChatReferencesState } from "../../../domains/gpt/hooks/useChatReferences";
import {
  TYPEWRITER_SPEED,
  useTypewriter,
} from "../../../domains/gpt/hooks/useTypewriter";
import type { ChatSuggestion } from "../../../domains/gpt/hooks/useChatSuggestion";
import { iosGenerateHaptic } from "../../../tools/ios";
import { HapticEffect } from "../../../interfaces";
import { IconShieldOff } from "@tabler/icons-react";
import { Button } from "../../ui/Button";
import {
  PatientDataBanner,
  type PatientDataBannerContent,
} from "./PatientDataBanner";
import { RedactedQuestion } from "./RedactedQuestion";
import type { UnsentReason } from "../../../domains/assistant/useQuestionRedaction";

// The shared conversation view for BOTH chat surfaces (general Juisci GPT +
// Chat-with-Paper). It groups the flat `chat.messages` into Q/R turns, renders
// the floating Réponse/Sources tabs (`GPTConversationTabs`), the live
// streaming-step + follow-ups (trailing), the end-of-scroll disclaimer, and the
// composer
// (footer). Surface differences are config props — no surface branching inside.
// The page shell owns `ScrollableLayout`/header/scroll anchor.

// Whether the desktop context panel is open: a reading preference, so it
// outlives the session.
const useContextPanelOpen = createLocalStore(
  "gpt-context-panel-open",
  true,
  z.boolean()
);

// Stable DOM id of the composer's textarea, so in-conversation actions (the
// onboarding getting-started "Ask a medical question" button) can focus it.
export const GPT_COMPOSER_INPUT_ID = "gpt-composer-input";

export type GptConversationProps = {
  chat: GptChatInstance;
  // The stream broke on a turn the server is still writing, and the surface is
  // waiting for the persisted answer (`useGptStreamRecovery`). The turn keeps
  // reading as in flight — reconnecting step, composer locked — instead of
  // showing the failure surface, because there is no failure yet. False on the
  // surfaces that have no persisted turn to wait for.
  isReconnecting: boolean;
  // The recovery gave the turn up (`useGptStreamRecovery`): the window closed
  // without the answer landing, or the server refused it. Shows the failure
  // surface even when the chat's own status cannot say so — a turn this surface
  // never streamed (a cold mount onto a turn in flight), or a stream that died
  // in silence and was stopped by the recovery. False on the surfaces that have
  // no persisted turn to wait for.
  hasLostTurn: boolean;
  // Whether the composer rides the software keyboard. False while ANOTHER
  // surface owns it — the history drawer's search field, which pushes this whole
  // conversation aside: the keyboard is a window-level signal, so without this
  // the pushed-aside composer would ride up for a field it does not own.
  followKeyboard: boolean;
  // Rendered above the first turn in the ANSWER tab (e.g. the desktop back row),
  // or null.
  leading: React.ReactNode;
  // The right end of the desktop sticky header row — the conversation's own
  // actions (Share, and the owner's menu). Null on the read-only surface;
  // Chat-with-Paper offers Stop sharing alone, while its conversation is shared.
  headerActions: React.ReactNode;
  // Rendered after the conversation turns and before follow-ups/disclaimer,
  // INCLUDING while a turn is streaming. It used to be suppressed for the whole
  // duration of a stream, which is not a decision this container can make for
  // its callers: Chat-with-Paper's visual-abstract card is meant to be offered
  // beside the answer being written, and hiding it until the stream settled put
  // it out of reach of every reader who left mid-answer. A caller that wants it
  // gone during a turn gates its own node. The only other caller — the
  // onboarding getting-started block — already does, on the opener being the
  // conversation's only message: that opener is generated server-side and
  // PERSISTED (POST /conversations/onboarding-opener), so the client never
  // streams it, and a length of 1 and a turn in flight cannot coincide there.
  afterTurns: React.ReactNode;
  // Non-null in Chat-with-Paper: the grounding paper renders as the CWYOP card
  // in the sources list with this label ("Chat With Paper" / "Chat with
  // abstract"), and the citations are numbered over the other sources.
  cwyopPaper: { articleId: string; label: string } | null;
  answerMath: boolean;
  actions: {
    onCopy: (question: string, answer: string) => void;
    onShare: () => void;
    // Whether to surface the Share button (otherwise hidden). Owner-side
    // JuisciGPT conversations flip this on behind the EXPERIMENTAL flag.
    showShare: boolean;
    // Replay the latest answer in place (only offered on the last turn). Null
    // on surfaces that don't support it (e.g. the new-chat landing).
    onRegenerate: (() => void) | null;
    // Retry after a stream error (re-ask the failed question). Null where
    // unsupported.
    onRetry: (() => void) | null;
    // Open the expert-annotation sheet for one turn. Null on the surfaces that
    // do not annotate: shared conversations and chat with paper.
    //
    // The turn is passed UP rather than looked up by the page: this component
    // already pairs user and assistant messages to compute `turnIndex`, and the
    // page would have to redo that pairing to find the question and answer for
    // an index. The page owns the conversation id (`viewStateKey` cannot serve —
    // it is an article id on Chat-with-Paper) and the existing verdicts.
    onEvaluate:
      | ((turn: {
          turnIndex: number;
          question: string;
          answer: string;
        }) => void)
      | null;
    // Which turns this reader has already judged, so the action bar can say
    // "revise" rather than "evaluate". Empty for everyone outside the pilot.
    evaluatedTurnIndexes: readonly number[];
    comingSoon: () => void;
  };
  // Undefined => the surface has no follow-ups (e.g. anonymous CWYOP, which has
  // no private endpoint).
  followUps: {
    enabled: boolean;
    questions: string[];
    onPick: (question: string) => void;
  } | null;
  disclaimer: string;
  background: string;
  roomId: string | null;
  // The assistant conversation this surface shows, whose runs list every source
  // they captured. Null on the other surfaces.
  assistantConversationId: string | null;
  // What the Evidence tab shows of the check of each of its runs' answers, by
  // run id. A run without an entry has no Evidence tab. Empty on the other
  // surfaces.
  evidenceCheckByRunId: ReadonlyMap<string, ShownEvidenceCheck>;
  onOpenSources: () => void;
  // Identifies the conversation for Sources-tab / scroll persistence across a
  // round-trip to a source article (conversation id / paper id).
  viewStateKey: string;
  // What the fixed bottom band offers when the agent is not proposing anything
  // (a `suggestion` takes the band over). Whatever sits there is what the
  // transcript's bottom padding is measured against, so a read-only surface
  // states its stand-in here rather than rendering a look-alike bar of its own
  // that the transcript cannot see.
  composer:
    | {
        kind: "input";
        // The page-owned submit (clears follow-ups, sends the message, fires
        // analytics). The composer and the follow-up cards both call it.
        onSubmit: (question: string) => void;
        // The stop button while streaming. Usually `() => chat.stop()`, but the
        // new-chat landing abandons the in-flight turn back to its landing state
        // instead, so the page owns the exact behavior.
        onStop: () => void;
        placeholder: string;
      }
    | {
        // The assistant engine's input: every question goes through Redaction first.
        kind: "assistant";
        onSubmit: (question: string) => void;
        onStop: () => void;
        placeholder: string;
        // The patient data banner at the top of the composer (AD-18).
        banner: PatientDataBannerContent | null;
        // The Questions that stayed on the device, oldest first: the device detector
        // failed (OD-04), or the server could not check them (AD-20). Each one shows
        // unsent after the Questions sent before it.
        unsent: ReadonlyArray<{
          id: string;
          question: string;
          questionCount: number;
          reason: UnsentReason;
          // Null on all but the latest, and on the latest once a Question follows it.
          retry: (() => void) | null;
          // Takes the message off the transcript once its text is back in the composer.
          edit: () => void;
        }>;
        // The mention lookup must not send a token that sits inside a Match. Null when Redaction is off.
        overlapsMatch:
          ((text: string, start: number, end: number) => boolean) | null;
      }
    | {
        // A read-only surface's stand-in for the input — the public shared
        // view's "Resume the discussion" button.
        kind: "cta";
        content: React.ReactNode;
      };
  // Whether the composer offers the Mode Router picker
  // (docs/gpt-mode-router.md). Behind the EXPERIMENTAL flag on the JuisciGPT
  // surfaces; always false where mode is not a choice (the paper chat is
  // grounded in one paper, onboarding is not a medical question).
  showModePicker: boolean;
  // Mode Router (docs/gpt-mode-router.md): rich Evidence-turn graphs keyed by
  // turnIndex (assistant-message ordinal), merged from the unified hydrate
  // side-car + this session's live Evidence sends. A turn whose index is present
  // renders via `EvidenceAnswer` instead of the general markdown path.
  evidenceGraphByTurnIndex: Map<number, EvidenceGraphView>;
  onOpenEvidenceSource: (
    source: EvidenceSourceView,
    citationNumber: number,
    quotes: readonly string[]
  ) => void;
  // An Evidence turn whose blocking call is in flight (no stream), or null. Shown
  // as a working turn at the tail; becomes a real transcript turn on finish.
  // `renderQuestion` is false on an Auto→Evidence redispatch (the general turn
  // already shows the question) — the working card is then the loader alone.
  pendingEvidence: { question: string; renderQuestion: boolean } | null;
  // Mode Router Search turns (find_publication), keyed by turnIndex: framing +
  // the found article cards (rendered as the /search rows) + refinement chips.
  searchByTurnIndex: Map<
    number,
    {
      framing: string;
      docs: GptSearchDoc[];
      pubmedDocs: GptSearchPubmedDoc[];
      refinements: string[];
    }
  >;
  // A forced-Search turn whose blocking call is in flight, or null.
  // A Search turn whose blocking call is in flight, or null. Same contract as
  // `pendingEvidence`: `renderQuestion` is false on an Auto→Search redispatch,
  // where the answerless general turn already shows the question.
  pendingSearch: { question: string; renderQuestion: boolean } | null;
  onOpenSearchArticle: (article: GptSearchDoc) => void;
  onRefineSearch: (refinement: string) => void;
  // Re-ask a zero-match Search turn's question in General mode (wider literature).
  // Null on read-only surfaces (shared view, chat-with-paper) that cannot start a
  // turn: the zero-match recovery button is then dropped, like the refinement chips.
  onAskGeneral: ((question: string) => void) | null;
  // Answers the assistant engine's question card. Null where no card can be
  // answered: the legacy engines ask none, and the shared view is read-only.
  onAnswerCard: ((toolCallId: string, text: string) => void) | null;
  // From the page's `useChatScrollAnchor`: the scroller it is wired to, and
  // the jump-to-bottom button.
  scrollRef: React.RefObject<HTMLDivElement | null>;
  isAtBottom: boolean;
  onScrollToBottom: () => void;
  // Cosmetic typewriter reveal for ONE persisted assistant message (the
  // onboarding opener on its first landing). Null everywhere else — the message
  // then renders statically. The reveal never involves the chat transport.
  revealAssistantId: string | null;
  // The agent's proposal for the latest turn, or null. When present it TAKES
  // OVER the composer band (the input is swapped out for the card), so the
  // proposal reads as the turn's pending question instead of a card lost in the
  // scrollback. ONE proposal at a time by construction — the band cannot render
  // two, so the type cannot express two. Null on surfaces that never propose
  // (the new-chat landing redirects mid-stream; the shared view is read-only).
  // Every exit of the card restores the input.
  suggestion: ChatSuggestion | null;
  // A status strip stacked ABOVE the input, not in place of it — for something
  // the surface is DOING rather than proposing. A proposal takes the band over
  // (`suggestion`); a status must not, because it would take the composer away
  // for the whole time the work runs. Chat-with-Paper reports a poster being
  // generated here. The conversation pages hold the phone's acquisition ask
  // here, for the same reason: it must not take the composer away.
  //
  // It is here rather than in the transcript for the reason the offer is: the
  // conversation pins the latest question to the top and streams the answer into
  // reserved slack below it, so a status rendered after the turns is off screen
  // exactly while the reader is waiting for it.
  composerNotice: React.ReactNode;
  // The push opt-in ask, or null when this user must not be asked (already
  // answered, permission already decided, or a surface that never asks).
  //
  // NOT a `suggestion` arm, deliberately: a proposal TAKES OVER the band, and
  // during a turn the band is the composer — with its stop button. This stacks
  // ABOVE the input instead, next to the mention dropdown and the attachment
  // chips, so it can appear the moment the agent starts thinking without taking
  // away the only control that matters right then. It follows that an agent
  // proposal still wins: the whole input arm, this card included, gives way.
  pushOptIn: { onDone: () => void } | null;
  // File attachment, from the page's `useChatAttachments`. Null on surfaces that
  // cannot take files: the read-only shared view, and Chat-with-Paper (which is
  // already scoped to one paper — a second document there would quietly compete
  // with the paper the whole surface is about).
  attachments: ChatAttachmentsState | null;
  // Whether the user may ADD or REMOVE files right now. Separate from
  // `attachments` because "show what is attached" and "let it be changed" are
  // different questions: the landing page must block a mid-stream pick (it would
  // change the chat's transport id and remount `useChat`, discarding the answer
  // being written) — but passing `attachments: null` to achieve that also hid
  // files the running turn was grounding on, which reads as "my file was lost".
  canModifyAttachments: boolean;
  // Referenced Juisci sources (playlists, articles), from the page's
  // `useChatReferences`. Null on surfaces that cannot reference anything: the
  // read-only shared view, and Chat-with-Paper (already scoped to one paper).
  // Parallel to `attachments`.
  references: ChatReferencesState | null;
  // The one article the conversation is about, when it references that and
  // nothing else (Figma "D2.conversation.about-study"): a card above the turns
  // in place of its reference chip. Null everywhere else.
  study: GptStudy | null;
  // Whether the user may ADD or REMOVE references right now — same rationale as
  // `canModifyAttachments` (a mid-stream add on the landing page would remount
  // `useChat` and discard the streaming answer).
  canModifyReferences: boolean;
};

/** The band's single proposal slot — one card per suggestion kind. */
function SuggestionCard({
  suggestion,
}: {
  suggestion: ChatSuggestion;
}): React.JSX.Element {
  switch (suggestion.kind) {
    case "topics":
      return (
        <TopicSuggestionCard
          topics={suggestion.topics}
          onConfirm={suggestion.onConfirm}
          onDismiss={suggestion.onDismiss}
        />
      );
    case "room":
      return (
        <RoomSuggestionCard
          room={suggestion.room}
          onShown={suggestion.onShown}
          onOpen={suggestion.onOpen}
          onDismiss={suggestion.onDismiss}
        />
      );
    case "visual_abstract":
      return (
        <VisualAbstractSuggestionCard
          articleId={suggestion.articleId}
          onShown={suggestion.onShown}
          onGenerate={suggestion.onGenerate}
          onDismiss={suggestion.onDismiss}
        />
      );
    default:
      throw new Error(suggestion satisfies never);
  }
}

export function GptConversation(
  props: GptConversationProps
): React.ReactElement {
  const { t } = useTranslation();
  const composer = useChatComposer("");
  // The reason of an unsent message, in the words the reader acts on.
  const unsentReasonText = (reason: UnsentReason): string => {
    switch (reason) {
      case "check_failed":
        return t("Message not sent: the patient data check failed.");
      case "check_unavailable":
        return t(
          "Message not sent: the patient data check is unavailable. Edit your message or try again."
        );
      default:
        throw new Error(reason satisfies never);
    }
  };
  // The source sheet lives here rather than inside the Sources tab: a citation
  // chip in the ANSWER opens it too, and the tab that holds the list is not
  // mounted then.
  const [selectedSource, setSelectedSource] = useSelectedGptSource();
  const references = props.references;
  const mention = useChatMention({
    enabled: references !== null && props.canModifyReferences,
    value: composer.value,
    setValue: composer.setValue,
    onAdd: (kind, targetId, title) => {
      if (references !== null) void references.onAdd(kind, targetId, title);
    },
    referencedKeys: references === null ? [] : references.referencedKeys,
    overlapsMatch:
      props.composer.kind === "assistant" ? props.composer.overlapsMatch : null,
  });
  // Emptiness is ChatInput's own business; block only on an in-flight upload OR
  // reference (the question would reach the agent before the file/playlist/
  // article it is about), or while a blocking Evidence turn is running (no
  // stream to interrupt).
  const isSendBlocked =
    (props.attachments !== null && props.attachments.isBusy) ||
    (props.references !== null && props.references.isBusy) ||
    props.pendingEvidence !== null ||
    props.pendingSearch !== null;
  const dictation = useDictationControl({
    onTranscript: composer.setValue,
    textareaRef: composer.ref,
    onSend: () => {
      iosGenerateHaptic(HapticEffect.SUCCESS);
      if (props.composer.kind === "cta")
        throw new Error("should not happen: send from the cta band");
      // A Question the detector fails on moves to an unsent bubble.
      props.composer.onSubmit(composer.value);
      composer.reset();
    },
    canSend: () =>
      props.composer.kind !== "cta" &&
      composer.value.length > 0 &&
      !isStreaming &&
      !isSendBlocked,
  });
  const add = useChatComposerAdd();
  // The composer "+" (File / Playlist / Article) leads the button row when both
  // files AND references can be added — it owns file picking, so `attach` goes
  // null in that case. Resolved once: its control and its panel are handed to the
  // composer separately, and must agree on being offered at all.
  const composerAdd =
    props.attachments !== null &&
    props.references !== null &&
    props.canModifyAttachments &&
    props.canModifyReferences
      ? { attachments: props.attachments, references: props.references }
      : null;
  const [mode, setMode] = useGptMode();
  const modeChoice = props.showModePicker
    ? { selected: mode, onSelectChange: setMode }
    : null;
  // The desktop composer keeps only "+", dictation and send in its button row
  // (Figma), so the mode moves into the "+" menu there. Only where that menu
  // exists: without it the picker stays in the row rather than vanishing.
  const isModeInAddMenu = isDesktopShell() && composerAdd !== null;
  // The desktop shell frames the conversation with the context panel instead
  // of the tabs (see GPTConversationTabs). Its open state is kept here, because
  // the composer band this component owns keeps clear of it too.
  const [isContextPanelOpen, setContextPanelOpen] = useContextPanelOpen();
  // At every width of the desktop shell: the tabs are the phone's. On a narrow
  // window the reader closes the panel from the top bar to widen the transcript.
  const contextPanel = isDesktopShell()
    ? {
        isOpen: isContextPanelOpen,
        setOpen: setContextPanelOpen,
      }
    : null;
  const { isKeyboardOpen } = useKeyboardHeight();
  // The band is fixed over the scroller, so the transcript has to reserve its
  // height at the bottom or its tail sits under it, unreachable. The band's
  // height is not knowable ahead of render — a proposal card is far taller than
  // the input, the input itself grows with a multi-line question, and the
  // safe-area reserve is device-dependent — so it is measured rather than
  // guessed.
  const { ref: bandRef, height: bandHeight } = useMeasuredHeight();
  const { isStreaming, isAwaitingFirstToken, streamingSteps } =
    deriveStreamState(props.chat, props.isReconnecting);

  const onRegenerate = props.actions.onRegenerate;
  // Read out of props once, like `onRegenerate`: narrowing `props.actions.x` to
  // non-null does not survive into a closure created later in the render.
  const onEvaluate = props.actions.onEvaluate;
  const onRetry = props.actions.onRetry;
  // Same reason as `onEvaluate`: read the nullable capability out of props once so
  // the `!== null` narrowing survives into the Search-turn closure below.
  const onAskGeneral = props.onAskGeneral;
  const onAnswerCard = props.onAnswerCard;

  // Group the flat message list into Q/R turns the tabs component frames. A user
  // message opens a turn; the following assistant message fills it.
  const lastMessage = props.chat.messages.at(-1);
  // The desktop asks the agent's open question above the composer, not in the
  // transcript: the reader answers it there, with an option or in the composer.
  // A band without the composer (a call to action, a proposal) leaves it in the transcript.
  const isCardInBand =
    isDesktopShell() &&
    onAnswerCard !== null &&
    props.composer.kind !== "cta" &&
    props.suggestion === null;
  const bandCard =
    !isCardInBand ||
    lastMessage === undefined ||
    lastMessage.role !== "assistant"
      ? null
      : uiMessageRequest(lastMessage);
  // The Juisci articles the answers cite, for the citations' hover cards
  // (desktop): the same batch read the Sources list makes for its rows.
  const { articleLink } = usePageLinks(props.roomId);
  const citedArticleIds = [
    ...new Set(
      props.chat.messages
        .flatMap((message) =>
          message.role === "assistant" ? uiMessageSources(message) : []
        )
        .flatMap((source) =>
          source.provenance === "juisci_internal" ? [source.id] : []
        )
    ),
  ];
  const citedArticles = useApiQuery(
    "/webapp/public/articles/batch",
    citedArticleIds.length === 0 || !isDesktopShell()
      ? false
      : { query: { ids: citedArticleIds.join(",") }, durable: true }
  );
  const citedArticleById = new Map(
    citedArticles.state === "success"
      ? citedArticles.body.map((article) => [article.id, article])
      : []
  );
  const lastMessageId = lastMessage === undefined ? null : lastMessage.id;
  // A live legacy turn carries no generation time, so it is timed around its stream (see useStreamDuration).
  const streamDuration = useStreamDuration(isStreaming, lastMessageId);

  // Cosmetic typewriter reveal for the designated persisted message (the
  // onboarding opener). `find` yields nothing when revealAssistantId is null, so
  // the reveal is inert on every other surface.
  const revealMessage = props.chat.messages.find(
    (message) => message.id === props.revealAssistantId
  );
  const revealFullText =
    revealMessage === undefined ? "" : uiMessageText(revealMessage);
  const reveal = useTypewriter(
    revealFullText,
    props.revealAssistantId !== null && revealFullText !== "",
    TYPEWRITER_SPEED.answer,
    0
  );
  const pairs: Array<{
    user: GeneralChatUIMessage | null;
    assistant: GeneralChatUIMessage | null;
  }> = [];
  for (const message of props.chat.messages) {
    const last = pairs.at(-1);
    if (message.role === "user") {
      pairs.push({ user: message, assistant: null });
    } else if (last !== undefined && last.assistant === null) {
      last.assistant = message;
    } else {
      pairs.push({ user: null, assistant: message });
    }
  }

  // A turn's index = its ordinal among ANSWERED (assistant) turns — the same key
  // the server writes Evidence side-car rows under (docs/gpt-mode-router.md), so
  // a turn whose index is in `evidenceGraphByTurnIndex` is an Evidence turn.
  let answeredOrdinal = 0;
  // The Questions asked up to each pair's cards: an unsent message follows that many.
  let questionCount = 0;
  const pairsWithIndex = pairs.map((pair) => {
    const turnIndex = pair.assistant !== null ? answeredOrdinal : null;
    if (pair.assistant !== null) answeredOrdinal += 1;
    if (pair.user !== null) questionCount += uiMessageQuestionCount(pair.user);
    const asked = questionCount;
    if (pair.assistant !== null)
      questionCount += uiMessageQuestionCount(pair.assistant);
    const cards = pair.assistant === null ? [] : uiMessageCards(pair.assistant);
    return { ...pair, turnIndex, asked, cards };
  });
  // An unsent message shows at the card that was open when it failed, else after its pair.
  const unsentSlot = (failedAt: number) => {
    const pairIndex = pairsWithIndex.findLastIndex(
      (pair) => pair.asked <= failedAt
    );
    if (pairIndex === -1)
      return pairsWithIndex.length === 0
        ? ({ kind: "trailing" } as const)
        : ({ kind: "aboveTurns" } as const);
    const pair = pairsWithIndex.at(pairIndex);
    if (pair === undefined)
      throw new Error(`should not happen: no pair at ${String(pairIndex)}`);
    const cardIndex = failedAt - pair.asked;
    if (cardIndex < pair.cards.length)
      return { kind: "atCard", pairIndex, cardIndex } as const;
    return pairIndex === pairsWithIndex.length - 1
      ? ({ kind: "trailing" } as const)
      : ({ kind: "afterPair", pairIndex } as const);
  };

  const unsent =
    props.composer.kind === "assistant" ? props.composer.unsent : [];
  // The page scrolls a new unsent message into view: it adds no turn to pin.
  const onScrollToBottom = props.onScrollToBottom;
  const revealUnsent = useCallback(
    (node: HTMLDivElement | null): void => {
      if (node !== null) onScrollToBottom();
    },
    [onScrollToBottom]
  );
  // The unsent messages whose slot matches `where`.
  const unsentMessages = (
    where: (slot: ReturnType<typeof unsentSlot>) => boolean
  ) =>
    unsent
      .map((entry) => ({ entry, slot: unsentSlot(entry.questionCount) }))
      .filter(({ slot }) => where(slot))
      .map(({ entry, slot }) => (
        <div
          key={entry.id}
          // Only the retriable one is new: an older one remounts when a turn takes it.
          ref={entry.retry === null ? null : revealUnsent}
          // The cards' column already insets its content.
          className={
            slot.kind === "atCard"
              ? "flex flex-col items-end gap-2"
              : "flex flex-col items-end gap-2 px-5"
          }
        >
          <ChatMessageMenu copyText={entry.question} className="w-fit">
            <ChatMessageCard variant={isDesktopShell() ? "desktop" : "default"}>
              {entry.question}
            </ChatMessageCard>
          </ChatMessageMenu>
          <div
            role="alert"
            className="flex items-center gap-2 font-dm-sans text-footnote text-ds-slate-700"
          >
            <IconShieldOff
              className="size-4 shrink-0 text-ds-clementine-700"
              stroke={1.5}
            />
            <p className="my-0">{unsentReasonText(entry.reason)}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // The reader corrects the text, then sends it again.
                composer.setValue(entry.question);
                entry.edit();
                const textarea = composer.ref.current;
                if (textarea !== null) textarea.focus();
              }}
            >
              {t("Edit")}
            </Button>
            {entry.retry !== null && (
              <Button
                variant="outline"
                size="sm"
                // A send during a turn would be dropped while the error clears.
                disabled={isStreaming}
                onClick={entry.retry}
              >
                {t("Retry")}
              </Button>
            )}
          </div>
        </div>
      ));

  const turns: ConversationTurn[] = pairsWithIndex.flatMap(
    ({ user, assistant, turnIndex, cards }, pairIndex) => {
      const id =
        assistant !== null ? assistant.id : user === null ? "" : user.id;
      if (id === "") return [];
      // The final pair has none: its unsent messages go after the trailing block.
      const unsentAfter = unsentMessages(
        (slot) => slot.kind === "afterPair" && slot.pairIndex === pairIndex
      );
      const evidenceGraph =
        turnIndex === null
          ? undefined
          : props.evidenceGraphByTurnIndex.get(turnIndex);
      const searchResult =
        turnIndex === null ? undefined : props.searchByTurnIndex.get(turnIndex);
      const question = user === null ? "" : uiMessageText(user);
      const isRevealing =
        assistant !== null && assistant.id === props.revealAssistantId;
      // The revealing message shows the streaming look (cursor, actions hidden)
      // until the typewriter finishes, mirroring a live answer.
      const isStreamingThisMessage = isRevealing
        ? !reveal.done
        : isStreaming && assistant !== null && assistant.id === lastMessageId;
      const published =
        assistant === null
          ? ""
          : isRevealing
            ? reveal.text
            : uiMessageText(assistant);
      // While the assistant engine writes, the reader reads its draft until the published text replaces it.
      const draft =
        assistant !== null && isStreamingThisMessage && published === ""
          ? uiMessageDraft(assistant)
          : null;
      const answerText = draft === null ? published : draft;
      // The pre-answer's place follows "What the webapp shows" (docs/juisci-gpt-pre-answer.md).
      // A failed pre-answer hides its text (OD-01): a cut sentence can say the opposite of its end.
      const preAnswer =
        assistant === null ? null : uiMessagePreAnswer(assistant);
      const preAnswerShown: React.ComponentProps<typeof PreAnswer> | null =
        preAnswer === null ||
        preAnswer.status === "failed" ||
        preAnswer.text === null
          ? null
          : {
              text: preAnswer.text,
              view:
                answerText !== ""
                  ? { kind: "folded" }
                  : isStreamingThisMessage
                    ? {
                        kind: "live",
                        isWriting: preAnswer.status === "writing",
                      }
                    : { kind: "unverified" },
            };
      const cleaned = cleanAnswer(answerText, t("See more"));
      // Replay is offered on the LAST turn only — regenerating an earlier answer
      // would truncate everything after it (matched server-side), which we don't
      // expose.
      const isLastTurn = assistant !== null && assistant.id === lastMessageId;
      const sources = assistant === null ? [] : uiMessageSources(assistant);
      // An answer of this conversation's own run opens its sources from the run's list.
      const assistantRun =
        props.assistantConversationId !== null &&
        assistant !== null &&
        assistant.metadata !== undefined &&
        assistant.metadata.runId !== null
          ? {
              conversationId: props.assistantConversationId,
              runId: assistant.metadata.runId,
            }
          : null;
      // The total rides the published answer; the stream then stays open for the turn suggestion.
      const sourceTotal =
        assistant === null ? null : uiMessageSourceTotal(assistant);
      // The check of this run's published answer: a care decision in source mode.
      const evidenceCheck =
        assistantRun === null || sourceTotal === null
          ? undefined
          : props.evidenceCheckByRunId.get(assistantRun.runId);
      if (
        assistantRun !== null &&
        !isStreamingThisMessage &&
        sources.length > 0 &&
        sourceTotal === null
      )
        throw new Error(
          "should not happen: a complete answer without its total"
        );
      // The run whose list the Sources tab shows, and its row total: a published answer of that run that cites a source.
      const listedRun =
        assistantRun === null || sourceTotal === null || sources.length === 0
          ? null
          : { ...assistantRun, sourceTotal };
      const openListed = (source: GptSourceRef, citationNumber: number) => {
        if (assistantRun !== null) {
          setSelectedSource({
            kind: "assistant",
            ...assistantRun,
            target: { kind: "cited", number: citationNumber },
          });
          return;
        }
        setSelectedSource({ kind: "listed", source, citationNumber });
      };
      // The answer's inline citations render as the NUMBER of the row they
      // point at in the Sources tab, and open the same sheet that row opens.
      // Chat-with-Paper numbers everything but the paper, which the tab draws
      // as its own card above the numbered rows.
      const numbering = citationNumbering(
        props.cwyopPaper === null
          ? sources
          : citedBesidesPaper(sources, props.cwyopPaper.articleId)
      );
      const citation: CitationResolver = ({ href, label }) => {
        const hit = numbering(href);
        // A citation the list does not hold still opens the sheet — on the
        // link alone. It is the only thing left that explains where the claim
        // came from, and it keeps the reader in the answer instead of throwing
        // them out to the publisher.
        if (hit === null)
          return {
            kind: "unlisted",
            onOpen: () => {
              setSelectedSource({ kind: "unlisted", href, label });
            },
          };
        return {
          kind: "listed",
          number: hit.number,
          onOpen: () => {
            openListed(hit.source, hit.number);
          },
          preview: isDesktopShell() ? (
            <SourceCard
              source={toChatSource(
                hit.source,
                hit.number - 1,
                citedArticleById,
                articleLink
              )}
              number={hit.number}
            />
          ) : null,
        };
      };
      // Callout panels are blockquote-marked sections of the SAME markdown
      // string, so they are split out here rather than rendered as quotes.
      const blocks = parseAnswerBlocks(cleaned, isStreamingThisMessage);
      // What the agent DID on this turn: the live tool parts while the turn is
      // still in this session's memory, the trace persisted with the transcript
      // once it has been reloaded. Either way the record outlives the stream,
      // which the old in-flight step indicator did not.
      const recorded =
        assistant === null
          ? { actions: [], seconds: null }
          : uiMessageActions(assistant);
      // The phone's trace stays as it was, without the questions the reader answered.
      const trace = isDesktopShell()
        ? recorded
        : {
            ...recorded,
            actions: recorded.actions.filter(
              (action) => action.kind !== "clarification"
            ),
          };
      // The agent waits on the reader's answer to its question on this turn,
      // in the band or in the transcript: the card's place is not the state.
      const isAsking =
        onAnswerCard !== null && cards.some((card) => card.answer === null);
      // The header shows its count and duration once the turn ends, so neither jumps while it streams.
      const sourceCount = isStreamingThisMessage
        ? null
        : listedRun === null
          ? sources.length
          : listedRun.sourceTotal;
      // The assistant engine's run spans several streams of one message (cards, resumes): only the server's seconds hold.
      const durationSeconds =
        isStreamingThisMessage || assistant === null
          ? null
          : assistant.metadata !== undefined ||
              streamDuration === null ||
              streamDuration.messageId !== assistant.id
            ? trace.seconds
            : streamDuration.seconds;
      const durationLabel =
        durationSeconds === null
          ? null
          : isDesktopShell()
            ? t("{{count}} s", { count: durationSeconds })
            : `${String(durationSeconds)}s`;
      // A fixed reply to a social or off-topic message has no answer to evaluate.
      const isFixedReply =
        assistant !== null &&
        assistant.metadata !== undefined &&
        assistant.metadata.outcome === "reply";
      // Both shells show the research live, open, until the answer's first
      // word (Figma "D2.conversation.generation"), when the dropdown takes over.
      // It stays while the agent waits on the reader's answer to its question.
      const showsLiveTrace =
        (isStreamingThisMessage || isAsking) && answerText === "";
      return [
        {
          id,
          question,
          sources,
          // The server emits `data-sources` only once the answer is complete
          // AND its citations have been resolved, so an answered turn holds no
          // sources for a second or two. Saying "no sources" in that window is
          // a lie the reader cannot tell from the truth. An assistant answer
          // waits for its run's list: its transcript sources lack the rest.
          sourcesPending:
            isStreamingThisMessage &&
            (assistantRun === null ? sources.length === 0 : listedRun === null),
          // Only the assistant engine streams a phase.
          isWriting:
            isStreamingThisMessage &&
            assistant !== null &&
            uiMessagePhase(assistant) === "writing",
          mode: ((): GptPanelMode | null => {
            // The assistant engine has no picker: its mode is how it classified
            // the question, which its message carries once the run has finished.
            const stakes =
              assistant === null || assistant.metadata === undefined
                ? null
                : assistant.metadata.stakes;
            switch (stakes) {
              case "care_decision":
                return { kind: "inferred", mode: "evidence" };
              case "informational":
                return { kind: "inferred", mode: "general" };
              case null:
                break;
              default:
                throw new Error(stakes satisfies never);
            }
            const chosen =
              evidenceGraph !== undefined
                ? "evidence"
                : searchResult !== undefined
                  ? "find_publication"
                  : // The general engine answers in text. An Auto turn routed
                    // to Evidence or Search opens with an empty answer and
                    // stays empty while that engine runs (or when it fails):
                    // naming it General then would be wrong.
                    props.showModePicker && answerText !== ""
                    ? "general"
                    : null;
            return chosen === null ? null : { kind: "chosen", mode: chosen };
          })(),
          trace: trace.actions,
          traceSummary:
            answerText === ""
              ? null
              : {
                  searchCount: gptSearchCount(trace.actions),
                  sourceCount,
                  durationLabel,
                },
          // An Evidence turn's tab bodies: the Evidence overlay gets the full
          // gated graph (claims, verified quotes, exclusions), the Sources
          // overlay its cited-source list. An assistant answer has an Evidence
          // tab only once its run has a check. Null otherwise.
          evidence:
            evidenceGraph !== undefined
              ? {
                  claimCount: evidenceGraph.claims.length,
                  loading: false,
                  content: (
                    <EvidenceDetailsPanel
                      answer={evidenceGraph}
                      onOpenSource={props.onOpenEvidenceSource}
                    />
                  ),
                }
              : evidenceCheck !== undefined
                ? {
                    claimCount:
                      evidenceCheck.scope === "pending"
                        ? null
                        : evidenceCheck.scope === "verified"
                          ? evidenceCheck.claims.length
                          : evidenceCheck.check.status === "ready"
                            ? evidenceCheck.check.claims.length
                            : evidenceCheck.check.status === "no_claims"
                              ? 0
                              : null,
                    loading: evidenceCheck.scope === "pending",
                    content: (
                      <AssistantEvidenceCheckPanel
                        shown={evidenceCheck}
                        sources={sources}
                        onOpenSource={openListed}
                      />
                    ),
                  }
                : null,
          // Once complete, an assistant answer lists every source its run
          // captured; the tab label counts the cited and the consulted sources.
          graphSources:
            evidenceGraph !== undefined
              ? {
                  count: evidenceSourceCount(evidenceGraph),
                  content: () => (
                    <EvidenceSourcesList
                      answer={evidenceGraph}
                      onOpenSource={props.onOpenEvidenceSource}
                    />
                  ),
                  run: null,
                }
              : listedRun === null
                ? null
                : {
                    count: listedRun.sourceTotal,
                    content: (size) => (
                      <AssistantSourcesList
                        conversationId={listedRun.conversationId}
                        runId={listedRun.runId}
                        size={size}
                        roomId={props.roomId}
                      />
                    ),
                    run: {
                      conversationId: listedRun.conversationId,
                      runId: listedRun.runId,
                    },
                  },
          content: (
            openSources: () => void,
            isContextPanelShown: boolean,
            isTraceInPanel: boolean
          ) => (
            <>
              {user !== null && (
                <ChatMessageMenu
                  copyText={question}
                  // Hugs the bubble against the right edge: the menu opens
                  // off this box.
                  className="mr-5 ml-auto w-fit"
                >
                  <ChatMessageCard
                    variant={isDesktopShell() ? "desktop" : "default"}
                  >
                    <RedactedQuestion text={question} />
                  </ChatMessageCard>
                </ChatMessageMenu>
              )}
              {/* Evidence turn (docs/gpt-mode-router.md): render the rich graph via
                EvidenceAnswer, mode badge before the grade. Otherwise the general
                markdown answer. A pre-token streaming turn has neither yet — only
                the question shows; the streaming-step indicator (trailing) stands
                in. */}
              {evidenceGraph !== undefined ? (
                <div className="flex flex-col gap-3 px-5">
                  {!isContextPanelShown && <GptModeBadge mode="evidence" />}
                  {/* Same entry point as a general answer: the source list
                      lives in the Sources tab, this row is the pointer to it.
                      Duration is never known — evidence turns are blocking. */}
                  {evidenceSourceCount(evidenceGraph) > 0 && (
                    <ChatActionsDropdown
                      variant={isDesktopShell() ? "desktop" : "default"}
                      // The Evidence engine runs its graph behind a single
                      // blocking call: it records no tool parts, so there is no
                      // trace to disclose and no stream to time. Its own
                      // reasoning is already on screen, in the graph below.
                      actions={[]}
                      searchCount={0}
                      sourceCount={evidenceSourceCount(evidenceGraph)}
                      durationLabel={null}
                      onOpenSources={openSources}
                    />
                  )}
                  <div className="selectable-text">
                    <EvidenceAnswer
                      answer={evidenceGraph}
                      onOpenSource={props.onOpenEvidenceSource}
                    />
                  </div>
                  {!isStreamingThisMessage && question !== "" && (
                    <ChatActions
                      onCopy={() => {
                        props.actions.onCopy(question, cleaned);
                      }}
                      onShare={props.actions.onShare}
                      showShare={props.actions.showShare}
                      onPlay={props.actions.comingSoon}
                      onThumbUp={props.actions.comingSoon}
                      onThumbDown={props.actions.comingSoon}
                      // Evidence turns are blocking — no in-place regenerate.
                      onRegenerate={undefined}
                      // An Evidence turn IS annotatable. The extra condition over
                      // the general branch is `answer !== null`: when nothing
                      // survived the fidelity gate this branch still renders (the
                      // claims and dropped edges are worth seeing) but there is no
                      // prose to score on seven criteria, and the server refuses it.
                      // Offering a button that 400s would be the worse bug.
                      //
                      // `cleaned` is only what the sheet DISPLAYS. The snapshot is
                      // built server-side from the side-car graph, because the
                      // transcript's copy of this answer has had its citation
                      // markers stripped.
                      onEvaluate={
                        onEvaluate === null ||
                        turnIndex === null ||
                        evidenceGraph.answer === null
                          ? undefined
                          : () => {
                              onEvaluate({
                                turnIndex,
                                question,
                                answer: cleaned,
                              });
                            }
                      }
                      evaluated={
                        turnIndex !== null &&
                        props.actions.evaluatedTurnIndexes.includes(turnIndex)
                      }
                    />
                  )}
                </div>
              ) : searchResult !== undefined ? (
                <GptSearchTurn
                  framing={searchResult.framing}
                  docs={searchResult.docs}
                  pubmedDocs={searchResult.pubmedDocs}
                  refinements={searchResult.refinements}
                  onOpenArticle={props.onOpenSearchArticle}
                  onRefine={props.onRefineSearch}
                  showModeBadge={!isContextPanelShown}
                  onAskGeneral={
                    onAskGeneral === null
                      ? null
                      : () => {
                          onAskGeneral(question);
                        }
                  }
                />
              ) : answerText !== "" ||
                cards.length > 0 ||
                showsLiveTrace ||
                preAnswerShown !== null ? (
                <div className="flex flex-col gap-3 px-5">
                  {/* Mode badge (docs/gpt-mode-router.md) — this branch is the
                    general engine, so the turn is a General turn. Only shown when
                    the picker is live (the unified surface). */}
                  {props.showModePicker && !isContextPanelShown && (
                    <GptModeBadge mode="general" />
                  )}
                  {cards.map((card, cardIndex) => {
                    const unsentAtCard = unsentMessages(
                      (slot) =>
                        slot.kind === "atCard" &&
                        slot.pairIndex === pairIndex &&
                        slot.cardIndex === cardIndex
                    );
                    // An answered card is the reader's later bubble; an open card keeps its options above.
                    return (
                      <Fragment key={card.toolCallId}>
                        {card.answer === null ? null : unsentAtCard}
                        {card.answer === null && isCardInBand ? null : (
                          <AskUserCard
                            question={card.input.question}
                            options={card.input.options}
                            answer={card.answer}
                            onAnswer={
                              onAnswerCard === null
                                ? null
                                : (text) => {
                                    onAnswerCard(card.toolCallId, text);
                                  }
                            }
                          />
                        )}
                        {card.answer === null ? unsentAtCard : null}
                      </Fragment>
                    );
                  })}
                  {/* The research progress stays under the pre-answer, where the answer will come. */}
                  {preAnswerShown !== null &&
                    preAnswerShown.view.kind !== "folded" && (
                      <PreAnswer {...preAnswerShown} />
                    )}
                  {showsLiveTrace && (
                    <ChatLiveProgress
                      actions={trace.actions}
                      phase={
                        isAsking
                          ? "clarifying"
                          : assistant === null
                            ? "searching"
                            : uiMessagePhase(assistant)
                      }
                      isTraceInPanel={isTraceInPanel}
                    />
                  )}
                  {answerText !== "" && (
                    <>
                      {(sources.length > 0 || trace.actions.length > 0) && (
                        <ChatActionsDropdown
                          variant={isDesktopShell() ? "desktop" : "default"}
                          actions={trace.actions}
                          searchCount={gptSearchCount(trace.actions)}
                          sourceCount={sourceCount}
                          durationLabel={durationLabel}
                          onOpenSources={openSources}
                        />
                      )}
                      {/* The blocks keep the answer's own spacing. */}
                      <div className="selectable-text flex flex-col gap-3">
                        {blocks.map((block, blockIndex) =>
                          block.kind === "callout" ? (
                            <CalloutPanel
                              key={blockIndex}
                              variant={isDesktopShell() ? "desktop" : "default"}
                              tone={block.tone}
                              language={block.language}
                              text={block.text}
                              math={props.answerMath}
                              streaming={isStreamingThisMessage}
                              citation={citation}
                              onCopy={() => {
                                props.actions.onCopy(question, block.text);
                              }}
                            />
                          ) : (
                            <ContentMarkdown
                              key={blockIndex}
                              className={
                                // The streaming caret belongs to the LAST block only —
                                // it marks where the text is still being written.
                                isStreamingThisMessage &&
                                blockIndex === blocks.length - 1
                                  ? GPT_ANSWER_CLASS + " typing"
                                  : GPT_ANSWER_CLASS
                              }
                              math={props.answerMath}
                              linkVariant="tag"
                              streaming={isStreamingThisMessage}
                              citation={citation}
                            >
                              {block.text}
                            </ContentMarkdown>
                          )
                        )}
                      </div>
                      {/* Answer actions (copy/rate/replay) belong to a real Q/R
                    answer. A question-less assistant turn — the new-chat intent
                    invitation or the onboarding opener — is not an answer, so it
                    carries no action bar (its own affordances live in
                    `afterTurns`). */}
                      {!isStreamingThisMessage && question !== "" && (
                        <ChatActions
                          onCopy={() => {
                            props.actions.onCopy(question, cleaned);
                          }}
                          onShare={props.actions.onShare}
                          showShare={props.actions.showShare}
                          onPlay={props.actions.comingSoon}
                          onThumbUp={props.actions.comingSoon}
                          onThumbDown={props.actions.comingSoon}
                          onRegenerate={
                            isLastTurn && onRegenerate !== null
                              ? onRegenerate
                              : undefined
                          }
                          // Both answering branches offer this now. The engine is no
                          // longer the gate — the server resolves it from the turn's
                          // `resolvedMode` and freezes the matching snapshot arm. What
                          // stays out is the Search branch, which has no answer to
                          // score and renders no action bar at all.
                          onEvaluate={
                            onEvaluate === null ||
                            turnIndex === null ||
                            isFixedReply
                              ? undefined
                              : () => {
                                  onEvaluate({
                                    turnIndex,
                                    question,
                                    answer: cleaned,
                                  });
                                }
                          }
                          evaluated={
                            turnIndex !== null &&
                            props.actions.evaluatedTurnIndexes.includes(
                              turnIndex
                            )
                          }
                        />
                      )}
                      {/* Under the answer, above the follow-up questions (OD-02). */}
                      {preAnswerShown !== null &&
                        preAnswerShown.view.kind === "folded" && (
                          <PreAnswer {...preAnswerShown} />
                        )}
                    </>
                  )}
                </div>
              ) : null}
              {unsentAfter}
            </>
          ),
        },
      ];
    }
  );

  const followUps = props.followUps;
  const trailing = (
    <>
      {/* A forced-Evidence turn whose blocking call is in flight: the question is
          on screen with a working loader (no stream), and becomes a real
          transcript turn once the graph lands (docs/gpt-mode-router.md). */}
      {props.pendingEvidence !== null && (
        <div className="flex flex-col gap-3">
          {props.pendingEvidence.renderQuestion && (
            <ChatMessageCard
              variant={isDesktopShell() ? "desktop" : "default"}
              className="mx-5"
            >
              {props.pendingEvidence.question}
            </ChatMessageCard>
          )}
          {/* The blocking engines stream no progress, so show the same rotating
              reasoning deck the general chat shows while thinking (not a static
              "reply shortly" loader). */}
          <GPTToolStep kind="thinking" />
        </div>
      )}
      {props.pendingSearch !== null && (
        <div className="flex flex-col gap-3">
          {props.pendingSearch.renderQuestion && (
            <ChatMessageCard
              variant={isDesktopShell() ? "desktop" : "default"}
              className="mx-5"
            >
              {props.pendingSearch.question}
            </ChatMessageCard>
          )}
          <GPTToolStep kind="thinking" />
        </div>
      )}
      {/* Once the answer's message exists, the live trace holds the research
          (see `showsLiveTrace`); the step below the turn then only says the
          stream is reconnecting. */}
      {(isStreaming &&
      lastMessage !== undefined &&
      lastMessage.role === "assistant" &&
      uiMessageText(lastMessage) === ""
        ? streamingSteps.filter((step) => step === "reconnecting")
        : streamingSteps
      ).map((step) => (
        <GPTToolStep key={step} kind={step} />
      ))}

      {props.afterTurns}

      {/* One component owns the whole failure presentation: out of credits is a
          REFUSAL (retrying cannot help) while anything else is an error worth a
          retry, and only the server's budget state can tell them apart.
          Suppressed while reconnecting: the transport has failed but the TURN has
          not, and offering Retry there would re-ask a question the server is
          still answering (409, plus a second billed turn). Also suppressed while a
          blocking Evidence/Search turn is in flight — an Auto→Evidence redispatch
          leaves the general chat "done" with no answer, and its Retry would 409
          against the Evidence turn holding the shared per-conversation lock. */}
      {(props.chat.status === "error" || props.hasLostTurn) &&
        !props.isReconnecting &&
        props.pendingEvidence === null &&
        props.pendingSearch === null && <GPTChatError onRetry={onRetry} />}

      {followUps !== null &&
        followUps.enabled &&
        !isStreaming &&
        followUps.questions.length > 0 && (
          <div className="flex flex-col gap-3 px-5 pt-6">
            {followUps.questions.map((question, index) => {
              const delay =
                index === 0
                  ? "delay-0"
                  : index === 1
                    ? "delay-150"
                    : "delay-300";
              return (
                <Fragment key={index}>
                  {/* Hairline between suggestions (Figma node 6066:2154) —
                      between, never before the first or after the last. */}
                  {index > 0 && (
                    <hr className="m-0 border-0 h-px bg-ds-black/18 opacity-50" />
                  )}
                  <FollowUpQuestionCard
                    question={question}
                    className={`opacity-0 animate-fade-in [animation-fill-mode:forwards] ${delay}`}
                    onClick={() => {
                      followUps.onPick(question);
                    }}
                  />
                </Fragment>
              );
            })}
          </div>
        )}

      {unsentMessages((slot) => slot.kind === "trailing")}
    </>
  );

  // The disclaimer closes the SCROLL, not the transcript: it rides below the pin
  // spacer (see GPTConversationTabs), so it comes to rest just above the
  // composer instead of floating mid-screen under a freshly pinned question.
  // Withheld until the answer has started — there is nothing to disclaim yet.
  // The desktop shell says it under the composer instead (GptDesktopNotice).
  const disclaimer =
    isAwaitingFirstToken || isDesktopShell() ? null : (
      <p className="text-center text-[10px] font-light text-[#212121] m-0">
        {props.disclaimer}
      </p>
    );

  // The band holds ONE widget at a time — a proposal takes it over from
  // whatever the surface normally offers. Keyed so `AnimatePresence` treats a
  // change as a replacement.
  function bandWidget(): { key: string; content: React.ReactNode } {
    const suggestion = props.suggestion;
    if (suggestion !== null)
      return {
        key: "suggestion",
        content: <SuggestionCard suggestion={suggestion} />,
      };
    const band = props.composer;
    switch (band.kind) {
      case "cta":
        return { key: "cta", content: band.content };
      case "input":
      case "assistant":
        return {
          key: "input",
          content: (
            <>
              {/* Its OWN AnimatePresence, not the band's. The band is wrapped
                  in `<AnimatePresence initial={false}>` to keep the input from
                  animating in on a cold load, and that `initial={false}` is
                  inherited through the presence context by every descendant —
                  a nested motion element would mount with no animation at all.
                  A fresh AnimatePresence here supplies its own context, so the
                  card fades and slides up as intended (and back out when the
                  user answers). */}
              <AnimatePresence>
                {props.pushOptIn !== null && (
                  <motion.div
                    key="push-opt-in"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ duration: 0.32, ease: "easeOut" }}
                  >
                    <PushSuggestionCard onDone={props.pushOptIn.onDone} />
                  </motion.div>
                )}
              </AnimatePresence>
              {/* Its own presence context, for the reason spelled out above the
                  push card: the band's `initial={false}` is inherited and would
                  mount this with no animation at all. */}
              <AnimatePresence>
                {props.composerNotice !== null && (
                  <motion.div
                    key="composer-notice"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ duration: 0.32, ease: "easeOut" }}
                  >
                    {props.composerNotice}
                  </motion.div>
                )}
              </AnimatePresence>
              {/* Its own presence context too, so the question slides in
                  above the composer and out once answered. */}
              <AnimatePresence>
                {bandCard !== null && onAnswerCard !== null && (
                  <motion.div
                    key={bandCard.toolCallId}
                    // Clear of the composer, so the two cards do not merge.
                    className="pb-3"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ duration: 0.32, ease: "easeOut" }}
                  >
                    <ChatOptions
                      title={bandCard.input.question}
                      select="single"
                      options={bandCard.input.options.map((label, index) => ({
                        id: String(index),
                        label,
                      }))}
                      selectedIds={[]}
                      onSelect={(id) => {
                        const label = bandCard.input.options[Number(id)];
                        if (label === undefined)
                          throw new Error(
                            `should not happen: option ${id} missing`
                          );
                        onAnswerCard(bandCard.toolCallId, label);
                      }}
                      // The composer below takes an answer in the reader's own words.
                      freeText={null}
                      onContinue={null}
                      continueLabel={t("Send")}
                      continueDisabled
                      onClose={null}
                      pager={null}
                      placement="band"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <ChatMentionDropdown
                results={mention.results}
                onSelect={mention.onSelect}
              />
              {props.attachments !== null && (
                <ChatAttachmentChips
                  attachments={props.attachments}
                  canRemove={props.canModifyAttachments}
                />
              )}
              {props.references !== null && (
                <ChatReferenceChips
                  references={props.references}
                  canRemove={props.canModifyReferences}
                />
              )}
              <ChatInput
                ref={composer.ref}
                className="w-full"
                variant={isDesktopShell() ? "docked" : "default"}
                id={GPT_COMPOSER_INPUT_ID}
                placeholder={band.placeholder}
                readOnly={dictation.composerReadOnly}
                onFocus={() => {
                  if (dictation.isActive) dictation.stop();
                }}
                value={composer.value}
                onChange={(e) => {
                  composer.setValue(e.target.value);
                }}
                mode={isModeInAddMenu ? null : modeChoice}
                leading={
                  composerAdd === null ? null : (
                    <ChatComposerAddTrigger
                      add={add}
                      attachments={composerAdd.attachments}
                      references={composerAdd.references}
                      disabled={false}
                      mode={
                        isModeInAddMenu && modeChoice !== null
                          ? modeChoice.selected
                          : null
                      }
                    />
                  )
                }
                leadingPanel={
                  composerAdd === null || add.state.view === "closed"
                    ? null
                    : {
                        id: add.panelId,
                        content: (
                          <ChatComposerAddPanel
                            add={add}
                            references={composerAdd.references}
                            mode={isModeInAddMenu ? modeChoice : null}
                          />
                        ),
                        onDismiss: add.close,
                        placement: "above",
                      }
                }
                banner={
                  band.kind === "assistant" && band.banner !== null ? (
                    <PatientDataBanner content={band.banner} />
                  ) : null
                }
                attach={
                  // Bare paperclip only on a files-but-no-references surface
                  // (none today); otherwise the "+" menu above owns attachment.
                  props.attachments !== null &&
                  props.canModifyAttachments &&
                  !(props.references !== null && props.canModifyReferences)
                    ? {
                        accept: ACCEPTED_FILE_TYPES,
                        isBusy: props.attachments.isBusy,
                        onPick: props.attachments.onPick,
                      }
                    : null
                }
                dictation={dictation.composer}
                stream={
                  isStreaming
                    ? { kind: "streaming", onStop: band.onStop }
                    : null
                }
                onSend={dictation.send}
                // An upload in flight blocks sending: the question would reach
                // the agent before the file it is about.
                sendDisabled={isSendBlocked}
              />
            </>
          ),
        };
      default:
        throw new Error(band satisfies never);
    }
  }

  const widget = bandWidget();
  const footer = (isContextPanelShown: boolean) => (
    <KeyboardAvoidingView
      behavior="position"
      enabled={props.followKeyboard}
      transitionDuration={0.22}
      style={
        // Left of the desktop context panel while it is on screen, so the
        // composer centres on the transcript rather than under the panel.
        isContextPanelShown
          ? {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: "var(--container-gpt-context-panel)",
            }
          : { position: "fixed", bottom: 0, width: "100%" }
      }
    >
      {/* The jump-to-bottom rides with the input: it exists so a user who
          scrolled up can get back to the answer they are about to reply to. */}
      <ScrollDownButton
        visible={!props.isAtBottom && props.composer.kind !== "cta"}
        onClick={props.onScrollToBottom}
        className="absolute -top-12 left-1/2 -translate-x-1/2 z-10"
      />
      {/* A read-only page ends on a button, not on an input card: on the
          desktop's solid band, the transcript fades out above it instead of
          being cut at the band's edge. */}
      {isDesktopShell() && props.composer.kind === "cta" ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-12 h-12 bg-linear-to-b from-transparent to-ds-canvas"
        />
      ) : null}
      {/* Keyboard open: the home indicator is covered by the keyboard, so the
          safe-area bottom reserve becomes dead space between the input and the
          keyboard — collapse it to a tight visual gap. */}
      <div
        ref={bandRef}
        className={cn(
          "w-full px-5",
          GPT_DESKTOP_COMPOSER,
          isDesktopShell()
            ? "flex flex-col gap-2 bg-ds-canvas pt-3 pb-4"
            : cn(
                "pt-2.5",
                isKeyboardOpen
                  ? "pb-2"
                  : "pb-[calc(16px+var(--safe-area-bottom)/2)]"
              )
        )}
      >
        {/* `mode="wait"` lets the outgoing widget finish before the incoming one
            slides up, so the swap reads as a replacement rather than a
            cross-fade. Same motion as the post-onboarding conversation's
            composer.

            The input's entrance is refused PER CHILD rather than by the
            presence's `initial={false}`, which suppressed it for EVERY child
            present on the first render. A proposal arriving from a stream is
            never present that early, but Chat-with-Paper's poster offer is: the
            article page has already warmed that query, so react-query answers
            from cache before the first paint and the card appeared with no
            motion at all. The input still arrives silent on a cold load. */}
        <AnimatePresence mode="wait">
          <motion.div
            key={widget.key}
            initial={widget.key === "input" ? false : { y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{
              duration: GPT_BAND_ENTRANCE_SECONDS,
              ease: "easeOut",
            }}
          >
            {widget.content}
          </motion.div>
        </AnimatePresence>
        {isDesktopShell() ? (
          <GptDesktopNotice
            reveal={props.composer.kind === "cta" ? "typed" : "static"}
          />
        ) : null}
      </div>
    </KeyboardAvoidingView>
  );

  const attachments = props.attachments;
  // Whether the transcript's above-turns slot has anything SETTLED to show — a
  // referenced source or an attached file. Drives both whether to render the
  // slot at all and its spacing (see `aboveTurns`).
  const hasAboveContent =
    (attachments !== null &&
      attachments.files.some((file) => file.status === "attached")) ||
    (props.references !== null &&
      props.references.references.some(
        (item) => item.status === "referenced"
      )) ||
    props.study !== null;
  return (
    <>
      {/* The open preview is a full-screen layer, rendered as a sibling of the
          conversation rather than inside its scroll flow. */}
      {attachments !== null &&
        attachments.previewing !== null &&
        attachments.conversation !== null && (
          <ChatAttachmentPreview
            conversation={attachments.conversation}
            file={attachments.previewing}
            onClose={attachments.onClosePreview}
          />
        )}
      <GPTConversationTabs
        turns={turns}
        trailing={trailing}
        disclaimer={disclaimer}
        aboveTurns={
          <>
            {/* Rendered only when there is SETTLED content — an empty wrapper is
                still a flex child of the column and would add a phantom gap before
                the first turn. `px-5` matches the message content's own inset (the
                column wrapper itself has no horizontal padding). */}
            {hasAboveContent ? (
              <div className="flex flex-col gap-3 px-5">
                {attachments !== null && (
                  <ChatAttachmentList attachments={attachments} />
                )}
                {props.study !== null ? (
                  <StudyCard study={props.study} />
                ) : props.references !== null ? (
                  <ChatReferenceList references={props.references} />
                ) : null}
              </div>
            ) : null}
            {/* A handed-over Question the detector failed on, before any turn. */}
            {unsentMessages((slot) => slot.kind === "aboveTurns")}
          </>
        }
        leading={props.leading}
        headerActions={props.headerActions}
        footer={footer}
        bandHeight={bandHeight}
        cwyopPaper={props.cwyopPaper}
        // An assistant conversation names its id; every other surface is the legacy engine's.
        engine={props.assistantConversationId === null ? "legacy" : "assistant"}
        background={props.background}
        roomId={props.roomId}
        onOpenSources={props.onOpenSources}
        viewStateKey={props.viewStateKey}
        scrollRef={props.scrollRef}
        contextPanel={contextPanel}
      />
      <GPTSourceSheet
        opened={selectedSource}
        roomId={props.roomId}
        onClose={() => {
          setSelectedSource(null);
        }}
      />
    </>
  );
}

export type GptStudy = {
  title: string;
  journal: string | null;
  year: string | null;
  // The article page, which "Back to the article" opens.
  link: string;
};

// What JuisciGPT reads in full for this conversation (Figma
// "context · article").
function StudyCard({ study }: { study: GptStudy }): React.JSX.Element {
  const { t } = useTranslation();
  const meta = [study.journal, study.year].filter((part) => part !== null);
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-ds-slate-100 bg-ds-slate-50 px-3.5 py-3 font-dm-sans">
      <p className="m-0 text-xs font-medium text-ds-slate-500">
        {t("About this study")}
      </p>
      <p className="m-0 text-sm font-semibold text-ds-slate-950">
        {study.title}
      </p>
      <div className="flex items-start gap-3 text-desktop-meta">
        {meta.length > 0 && (
          <span className="text-ds-slate-500">{meta.join(" · ")}</span>
        )}
        <Link
          to={study.link}
          className="font-semibold text-ds-mint-800 underline"
        >
          {t("Back to the article")}
        </Link>
      </div>
    </div>
  );
}
